import React from 'react';
import {
  Box,
  Typography,
  TextField,
  FormControl,
  Select,
  MenuItem,
  Checkbox,
  Chip,
} from '@mui/material';
import SingleDatePickerMAIQ from 'app/components/SingleDatePickerMAIQ';
import { parseDateValue } from 'utils/dateUtils';

export interface DealerSpecialFormData {
  special_headline: string;
  special_description: string;
  start_date: string;
  end_date: string;
  applicable_model: string[];
  disclaimer_text: string;
}

export interface DealerSpecialFormErrors {
  special_headline?: string;
  special_description?: string;
  start_date?: string;
  end_date?: string;
  applicable_model?: string;
  disclaimer_text?: string;
}

interface DealerSpecialFormProps {
  formData: DealerSpecialFormData;
  onFormChange: (field: string, value: any) => void;
  errors?: DealerSpecialFormErrors;
  disabled?: boolean;
}

const applicableModelOptions = ['CPO', 'New', 'Used', 'All'];

const DealerSpecialForm: React.FC<DealerSpecialFormProps> = ({
  formData,
  onFormChange,
  errors = {},
  disabled = false,
}) => {
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    onFormChange(name, value);
  };

  const handleApplicableModelChange = (event: any) => {
    const value = event.target.value;
    onFormChange(
      'applicable_model',
      typeof value === 'string' ? value.split(',') : value,
    );
  };

  const handleDateChange = (field: string) => (date: string | null) => {
    onFormChange(field, date || '');
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/* Special Headline */}
      <Box>
        <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
          Special Headline *
        </Typography>
        <TextField
          fullWidth
          name="special_headline"
          value={formData.special_headline}
          onChange={handleChange}
          disabled={disabled}
          required
          size="small"
          placeholder="Enter special headline"
          error={!!errors.special_headline}
          helperText={errors.special_headline}
          inputProps={{
            sx: { py: 1.5 },
          }}
        />
      </Box>

      {/* Special Description */}
      <Box>
        <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
          Special Description *
        </Typography>
        <TextField
          fullWidth
          multiline
          rows={3}
          name="special_description"
          value={formData.special_description}
          onChange={handleChange}
          disabled={disabled}
          required
          size="small"
          placeholder="Enter special description"
          error={!!errors.special_description}
          helperText={errors.special_description}
          inputProps={{
            sx: { py: 1.5 },
          }}
        />
      </Box>

      {/* Start Date */}
      <Box>
        <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
          Start Date *
        </Typography>
        <SingleDatePickerMAIQ
          value={formData.start_date}
          onChange={handleDateChange('start_date')}
          placeholder="Select start date"
          disabled={disabled}
        />
        {errors.start_date && (
          <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75, display: 'block' }}>
            {errors.start_date}
          </Typography>
        )}
      </Box>

      {/* End Date */}
      <Box>
        <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
          End Date *
        </Typography>
        <SingleDatePickerMAIQ
          value={formData.end_date}
          onChange={handleDateChange('end_date')}
          placeholder="Select end date"
          minDate={formData.start_date ? parseDateValue(formData.start_date) : undefined}
          disabled={disabled}
        />
        {errors.end_date && (
          <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75, display: 'block' }}>
            {errors.end_date}
          </Typography>
        )}
      </Box>

      {/* Applicable Model */}
      <Box>
        <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
          Applicable Model *
        </Typography>
        <FormControl fullWidth size="small" required error={!!errors.applicable_model}>
          <Select
            multiple
            value={formData.applicable_model}
            onChange={handleApplicableModelChange}
            disabled={disabled}
            renderValue={selected => (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {(selected || []).length === 0 ? (
                  <span style={{ color: 'rgba(0, 0, 0, 0.6)' }}>Select applicable model</span>
                ) : (
                  (selected || []).map((value: string) => (
                    <Chip key={value} label={value} size="small" />
                  ))
                )}
              </Box>
            )}
            sx={{
              '& .MuiOutlinedInput-root': {
                padding: '8px 14px',
              },
            }}
          >
            {applicableModelOptions.map(option => (
              <MenuItem key={option} value={option}>
                <Checkbox checked={formData.applicable_model.indexOf(option) > -1} />
                {option}
              </MenuItem>
            ))}
          </Select>
          {errors.applicable_model && (
            <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
              {errors.applicable_model}
            </Typography>
          )}
        </FormControl>
      </Box>

      {/* Disclaimer Text */}
      <Box>
        <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
          Disclaimer Text
        </Typography>
        <TextField
          fullWidth
          multiline
          rows={2}
          name="disclaimer_text"
          value={formData.disclaimer_text}
          onChange={handleChange}
          disabled={disabled}
          size="small"
          placeholder="Enter disclaimer text"
          error={!!errors.disclaimer_text}
          helperText={errors.disclaimer_text}
          inputProps={{
            sx: { py: 1.5 },
          }}
        />
      </Box>
    </Box>
  );
};

export default DealerSpecialForm;

